import React, { useMemo } from "react";
import pic1 from "../../Assets/Images/Pic1.jpg";
import intro from "../../Assets/Wealthgrow_Intro.mp4";

const services = [
  "Tax Optimization",
  "Financial Planning",
  "Legal Advisory",
  "Technology Consulting",
  "Engineering Consulting",
  "Business and Management Consulting",
];

const whyChooseUs = [
  {
    title: "Experienced & Multidisciplinary Expertise",
    desc: "Our team brings together professionals across taxation, finance, legal advisory, engineering, technology, and business consulting, ensuring you receive well-rounded and practical guidance under one roof.",
  },
  {
    title: "End-to-End Solutions",
    desc: "From compliance and planning to execution and support, we handle the entire journey, helping individuals and businesses focus on growth while we manage complexity.",
  },
  {
    title: "Client-First Approach",
    desc: "Every client is unique. We take time to understand your goals, challenges, and vision, and then deliver customized, result-oriented solutions instead of one-size-fits-all advice.",
  },
  {
    title: "Transparent & Cost-Effective",
    desc: "We believe in clarity and honesty. Our services are designed to be transparent, efficient, and value-driven, ensuring you get the best outcomes without unnecessary costs.",
  },
  {
    title: "Technology-Driven",
    desc: "By leveraging modern tools, automation, and data-driven insights, we simplify processes, improve accuracy, and keep you ahead in a rapidly evolving financial and regulatory landscape.",
  },
  {
    title: "Trust, Compliance & Reliability",
    desc: "With a strong focus on compliance and ethics, we help you navigate regulations confidently, reduce risks, and build a solid foundation for sustainable success.",
  },
];

const cardTone = (index) => {
  if (index === 0) {
    return "border border-accent-gold/40 bg-gradient-to-br from-primary-default to-primary-dark text-white shadow-xl";
  }

  return "border border-primary-light/20 bg-primary-dark text-white shadow-lg";
};

const AboutUs = () => {
  const highlightCards = useMemo(
    () =>
      whyChooseUs.map((item, index) => ({
        ...item,
        tone: cardTone(index),
      })),
    []
  );

  return (
    <div className="overflow-hidden bg-body animate-[pageFade_0.6s_ease-out]">
      <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:py-10">
        <div className="relative isolate mb-24 overflow-hidden rounded-[28px] border border-white/60 bg-white shadow-[0_24px_70px_rgba(16,42,107,0.08)]">
          <div className="absolute inset-0 bg-white/92" />
          <div className="absolute left-[-8%] top-[12%] h-56 w-56 rounded-full bg-accent-gold/12 blur-3xl" />
          <div className="absolute bottom-[-12%] right-[-4%] h-72 w-72 rounded-full bg-primary-default/8 blur-3xl" />

          <div className="relative z-10 grid items-center gap-8 px-5 py-8 sm:px-8 sm:py-10 lg:grid-cols-[minmax(0,1.1fr)_minmax(320px,460px)] lg:gap-12 lg:px-10">
            <div className="max-w-2xl">
              <div className="mb-4 inline-flex rounded-full border border-accent-gold/30 bg-accent-gold/10 px-4 py-2 text-xs font-semibold uppercase tracking-[0.28em] text-primary-default">
                About WealthGrow
              </div>
              <h2 className="mb-5 text-3xl font-bold leading-tight text-primary-dark sm:text-4xl lg:text-5xl">
                Empowering Your Financial
                <br />
                <span className="text-accent-gold">Growth & Success</span>
              </h2>

              <p className="mb-8 max-w-xl text-base leading-8 text-neutral-textSecondary sm:text-lg">
                At our core, we believe smart financial decisions build stronger
                futures. From taxation and compliance to financial planning and
                business advisory, we deliver practical solutions that help you
                grow, scale, and succeed with confidence.
              </p>

              <div className="grid gap-x-8 gap-y-5 sm:grid-cols-2">
                {services.map((service) => (
                  <div
                    key={service}
                    className="flex items-center gap-3 rounded-xl border border-primary-default/8 px-4 py-3 shadow-sm"
                  >
                    <div className="h-2.5 w-2.5 rounded-full bg-accent-gold" />
                    <span className="text-sm font-medium text-neutral-textSecondary sm:text-base">
                      {service}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex justify-center lg:justify-end">
              <div className="relative w-full max-w-[460px] rounded-[32px] border border-white/80 p-3 shadow-[0_28px_70px_rgba(16,42,107,0.16)] backdrop-blur-sm transition-all duration-500 ease-out hover:-translate-y-1 sm:p-4">
                <div className="absolute -inset-3 -z-10 rounded-[36px] bg-primary-default/8 blur-2xl" />
                <div className="overflow-hidden rounded-[24px] border border-neutral-border/60">
                  <video
                    src={intro}
                    autoPlay
                    playsInline
                    controls
                    muted
                    className="h-[260px] w-full object-cover sm:h-[340px] lg:h-[480px]"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mb-16">
          <div className="mb-12 text-center">
            <h2 className="mb-4 text-3xl font-bold text-neutral-textPrimary sm:text-4xl">
              What we offer?
            </h2>
            <p className="mx-auto max-w-3xl text-sm text-neutral-textSecondary sm:text-base">
              At Wealthgrow Fintax Services, we don&apos;t just offer consultancy.
              We become your long-term growth partner with an approach that
              blends expertise, transparency, and technology.
            </p>
          </div>

          <div className="grid gap-6 md:grid-cols-2 md:gap-8">
            <div className="space-y-5 text-sm leading-7 text-neutral-textSecondary sm:text-base">
              <p>
                Our advisory approach combines technical expertise with
                practical insight, enabling clients to navigate complex
                regulations, mitigate risk, and make informed strategic
                decisions with confidence.
              </p>
              <p>
                We serve individuals, startups, and established enterprises
                with a strong focus on regulatory compliance, financial
                discipline, and long-term value creation.
              </p>
            </div>

            <div className="min-h-[180px] rounded-2xl bg-gradient-to-br from-accent-goldLight to-accent-gold p-5 sm:p-6">
              <div className="flex h-full flex-col items-center justify-center rounded-xl bg-neutral-white p-4 text-center">
                <div className="mb-2 text-sm font-semibold uppercase tracking-[0.35em] text-primary-default">
                  Insight
                </div>
                <h3 className="mb-1 text-lg font-bold text-neutral-textPrimary sm:text-xl">
                  Fresh Ideas for Every Business
                </h3>
                <p className="text-neutral-textSecondary">
                  Innovative solutions tailored to your unique goals, stage, and
                  operating model.
                </p>
              </div>
            </div>
          </div>

          <div className="mt-12 text-center">
            <h2 className="text-3xl font-bold text-neutral-textPrimary sm:text-4xl">
              Why choose us?
            </h2>
          </div>

          <div className="mt-10 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {highlightCards.map((item, index) => (
              <article
                key={item.title}
                className={`rounded-2xl p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl sm:p-8 ${item.tone}`}
              >
                <h3
                  className="mb-4 text-center text-lg font-bold text-accent-gold"
                >
                  {item.title}
                </h3>
                <p
                  className="text-sm leading-relaxed text-white/85"
                >
                  {item.desc}
                </p>
              </article>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AboutUs;